/**
 * Tool info popovers — opened by the small (i) button on each card in the
 * Analysis panel. One source of truth for what a tool does, what data it
 * needs, and what it outputs.
 */

const TOOLS = {
  isochrone: {
    title: 'Isochrone analysis',
    blurb: 'Computes travel-time catchment polygons using a real road network (Valhalla) over the OSM extract baked into the server. The result is a set of nested polygons showing what is reachable within each interval.',
    requires: [
      'A click anywhere on the map (or on any visible point feature).',
      'Travel mode: driving / walking / cycling.',
      'Time intervals (default 15/30/60 min).',
    ],
    output:
      'Concentric polygons (one per interval) plus a per-visible-layer feature count for each ring — useful for catchment population, facility coverage, etc.',
  },

  buffer: {
    title: 'Buffer / proximity',
    blurb: 'Draws a circle of fixed radius around a clicked point, then tallies how many features from each visible vector layer fall inside.',
    requires: [
      'A click anywhere on the map.',
      'A radius (5 / 10 / 20 / 50 km).',
      'At least one visible vector layer to make the count meaningful.',
    ],
    output: 'A buffer polygon on the map plus a list showing how many features from each visible layer intersect it.',
  },

  timeSeries: {
    title: 'Time series',
    blurb: 'Animates any visible vector layer through time by filtering features at each step. Auto-detects the temporal column (year, date, month, timestamp) and steps through the unique values.',
    requires: [
      'A visible vector layer with a temporal column (e.g. `year`, `date`, `month`, `created_at`).',
      'At least 2 distinct values in that column.',
    ],
    output: 'A play/scrub time bar that filters the layer to one time step at a time. Works for points, lines, and polygons.',
  },

  ai: {
    title: 'AI spatial analysis',
    blurb: 'Sends a compact summary of your visible vector layers to Claude (Anthropic) and renders the response as markdown. Domain-agnostic — adapts to whatever you have loaded.',
    requires: [
      'At least one visible vector layer with attributes.',
      'An Anthropic API key configured on the server (`ANTHROPIC_API_KEY` env var).',
    ],
    output: 'A short markdown analysis: 3–5 patterns / outliers from the data, 2–3 concrete next analyses to run, and one caveat if relevant.',
  },

  odFlow: {
    title: 'Origin-Destination flows',
    blurb: 'Renders curved great-circle arcs between origin and destination points. Arc color and width scale with magnitude using equal-interval bins.',
    requires: [
      'EITHER a CSV (paste mode) with `origin_lat`, `origin_lng`, `dest_lat`, `dest_lng`, `magnitude` columns,',
      'OR a visible point layer where each feature is one OD pair (geometry as origin OR destination, plus columns for the other endpoint).',
      'A magnitude column is optional — defaults to 1 per flow.',
    ],
    output: 'Curved arcs on the map (5-step amber → red ramp), endpoint dots in teal (origin) / green (destination), and a legend with the magnitude bins.',
  },

  suitability: {
    title: 'Constraint &amp; opportunity (suitability) map',
    blurb: 'Combines multiple numeric columns from a polygon layer into a single suitability score per polygon. Each column is marked as Skip, Opportunity (weighted contribution; higher OR lower can be "better"), or Constraint (excludes the polygon when value > 0).',
    requires: [
      'A visible polygon layer.',
      'At least one numeric column to mark as opportunity or constraint.',
    ],
    output: 'A 5-bin choropleth on the polygon layer using a green ramp. Polygons hit by any constraint render at score 0.',
  },

  emergingHotspot: {
    title: 'Emerging hotspot',
    blurb: 'Builds a hexagonal space-time cube from a point layer and a temporal column. For each cell, runs the Mann-Kendall trend test on the count time series to classify the cell as Increasing / Decreasing / Stable / Sparse.',
    requires: [
      'A visible point layer with a temporal column (`year`, `date`, `month`, `timestamp`, etc.).',
      'At least 4 time bins of data per cell to test a trend (cells with fewer fall into the Sparse class).',
      'Up to ~50,000 features total (capped for in-browser performance).',
    ],
    output: 'A hex-grid overlay where each cell is colored by trend category. Includes counts in the legend so you can see the class breakdown at a glance.',
  },
};

export function openToolInfoModal(key) {
  const info = TOOLS[key];
  if (!info) return;

  document.querySelector('#toolInfoModal')?.remove();
  document.body.insertAdjacentHTML('beforeend', `
    <div class="modal-bg show" id="toolInfoModal">
      <div class="modal" style="width:480px;max-width:96vw">
        <h3 style="margin:0 0 8px">${info.title}</h3>
        <p style="font-size:12.5px;color:var(--text2);line-height:1.55;margin:0 0 14px">
          ${info.blurb}
        </p>

        <h4 class="ti-h4">Data required</h4>
        <ul class="ti-list">
          ${info.requires.map(r => `<li>${r}</li>`).join('')}
        </ul>

        <h4 class="ti-h4">What you get</h4>
        <p style="font-size:12px;color:var(--text2);line-height:1.55;margin:0 0 14px">
          ${info.output}
        </p>

        <div style="display:flex;justify-content:flex-end;margin-top:14px">
          <button class="btn" data-ti="close">Got it</button>
        </div>
      </div>
      <style>
        .ti-h4 { font-size: 9.5px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.14em; color: var(--text3); margin: 0 0 6px; }
        .ti-list { font-size: 12px; color: var(--text2); line-height: 1.55; margin: 0 0 14px 18px; padding: 0; }
        .ti-list li { margin-bottom: 3px; }
        .ti-list li code, p code {
          font-family: var(--mono); font-size: 11px;
          background: var(--bg4); padding: 1px 5px; border-radius: 3px; color: var(--text);
        }
      </style>
    </div>
  `);
  const root = document.getElementById('toolInfoModal');
  root.addEventListener('click', e => {
    if (e.target === root || e.target.closest('[data-ti="close"]')) root.remove();
  });
  document.addEventListener('keydown', escClose, { once: true });
  function escClose(e) { if (e.key === 'Escape' && document.getElementById('toolInfoModal')) root.remove(); }
}
